/**
 * Hand tracking via MediaPipe HandLandmarker.
 * Provides wrist positions for left/right hand attribution.
 */
import {
  HandLandmarker,
  FilesetResolver,
  type NormalizedLandmark,
} from '@mediapipe/tasks-vision';

export interface HandPosition {
  isLeft: boolean;
  x: number;
  y: number;
}

const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

const WRIST_INDEX = 0;

export class HandTracker {
  private landmarker: HandLandmarker | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
      this.landmarker = await HandLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: MODEL_URL },
        numHands: 2,
        minHandDetectionConfidence: 0.5,
        minHandPresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        runningMode: 'VIDEO',
      });
      this.initialized = true;
    })();

    return this.initPromise;
  }

  isReady(): boolean {
    return this.initialized && this.landmarker != null;
  }

  detect(video: HTMLVideoElement, timestampMs: number): HandPosition[] {
    if (!this.landmarker) return [];

    try {
      const result = this.landmarker.detectForVideo(video, timestampMs);
      const hands: HandPosition[] = [];

      for (let i = 0; i < (result.landmarks?.length ?? 0); i++) {
        const landmarks = result.landmarks[i];
        const handedness = result.handedness?.[i]?.[0]?.categoryName?.toLowerCase() ?? 'left';
        if (!landmarks || landmarks.length <= WRIST_INDEX) continue;

        const wrist = landmarks[WRIST_INDEX] as NormalizedLandmark;
        hands.push({
          isLeft: handedness === 'left',
          x: wrist.x * video.videoWidth,
          y: wrist.y * video.videoHeight,
        });
      }

      return hands;
    } catch {
      return [];
    }
  }
}
